"use client";

import type { JSX } from "react";
import { motion, type Variants } from "framer-motion";
import { Avatar, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Marquee } from "@/components/home-v3/ui/marquee";

const headingVariants: Variants = {
  hidden: { opacity: 0, filter: "blur(8px)" },
  visible: {
    opacity: 1,
    filter: "blur(0px)",
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
  },
};

const testimonialRows = [
  [
    {
      name: "Daryl Nehls",
      role: "Founder, TechMatter",
      quote:
        "“This team knows their stuff. I'm grateful for their guidance. The expertise and practice demonstrated by this team were invaluable to me.”",
      avatar: "/home-v3/daryl.png",
    },
    {
      name: "Marci Senter",
      role: "Lead Branding Consultant",
      quote:
        "“Our visual designer lets you quickly an of drag and drop your own way to custom apps for both keep desktop, mobile & also tab for th both of it has been useful.”",
        avatar: "/home-v3/marci.png",
      },
    {
      name: "Rayford Chenail",
      role: "Principal Interactions Assistant",
      quote:
        "“Life-changing experience! I learned so much from their program. Participating in their program was a transformative experience for me.”",
      avatar: "/home-v3/rayford.png",
    },
  ],
  [
    {
      name: "Freida Varnes",
      role: "Forward Mobility Liaison",
      quote:
        "“Efficient and professional. I will definitely use their services again. The professionalism of this team made the entire process effortless and stress-free.”",
      avatar: "/home-v3/freida.png",
    },
    {
      name: "Elmer Laverty",
      role: "National Data Executive",
      quote:
        "“An absolute pleasure to work with. They made the process seamless. Working with this team was a seamless and enjoyable experience.”",
      avatar: "/home-v3/elmer.png",
    },
    {
      name: "Darron Kulikowski",
      role: "Internal Configuration Director",
      quote:
        "“Prompt delivery and top-notch quality. Impressed with the speed and accuracy. The efficiency and speed at which they delivered the product were impressive.”",
      avatar: "/home-v3/darron.png",
    },
  ],
];

export const FounderTestimonialsSection = (): JSX.Element => {
  return (
    <section
      aria-labelledby="testimonials-heading"
      className="flex w-full flex-col items-start mt-20 px-1 sm:py-[60px]"
    >
      <div className="flex w-full flex-col gap-[60px]">
        <header className="flex w-full flex-col items-center gap-6 pt-6">
          <Badge className="animate-revolving-border h-auto rounded-[30px] border-0 bg-[#6a6a6a80] px-4 py-2 [font-family:'Suisse_Intl_Trial-Medium',Helvetica] text-sm font-medium leading-[23.4px] text-white backdrop-blur-[3.5px]">
            Testimonials
          </Badge>
          <motion.h2
            id="testimonials-heading"
            className="[font-family:'Suisse_Works_Trial-Regular',Helvetica] text-center text-3xl leading-[1.2] sm:text-4xl lg:text-5xl lg:leading-[64px] font-normal tracking-[-1.92px] text-white"
            variants={headingVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.6 }}
          >
            Trusted by business owners who
            <br />
            want clarity and structure
          </motion.h2>
        </header>
        <div className="flex w-full flex-col gap-[18px]">
          {testimonialRows.map((row, rowIndex) => (
            <Marquee
              key={`testimonial-row-${rowIndex}`}
              reverse={rowIndex === 0}
              pauseOnHover
              // Each copy is ~1276px wide (3 cards) but shifts by its own
              // width per cycle, so 2 copies leave the container short at the
              // end of every loop. 4 keeps ~3.8k px covered at all times.
              repeat={4}
              className="[--duration:32s] [--gap:0.5rem] py-1"
            >
              {row.map((testimonial) => (
                <Card
                  key={testimonial.name}
                  className="relative h-[234px] w-[300px] sm:w-[420px] shrink-0 overflow-hidden rounded-[32px] border-0 bg-[#0f1319] p-0 shadow-none"
                >
                  <div className="pointer-events-none absolute -bottom-[281px] left-9 h-72 w-[293px] rounded-[146.5px/144px] bg-[#0061fe] blur-[75px]" />
                  <div className="pointer-events-none absolute -right-[75px] top-px h-[171px] w-[171px] rounded-[85.61px] bg-[#6fa6ff] blur-[71.73px]" />
                  <CardContent className="relative z-10 flex h-full flex-col p-7 sm:px-10 sm:pt-10">
                    <div className="flex items-center gap-2">
                      <Avatar className="h-[52px] w-[52px] shrink-0 border border-white">
                        <AvatarImage
                          src={testimonial.avatar}
                          alt={`${testimonial.name} avatar`}
                          className="h-[50px] w-[50px] object-cover"
                        />
                      </Avatar>
                      <div className="min-w-0">
                        <p className="font-inter-tight text-base font-normal leading-[19.2px] tracking-[-0.48px] text-[#0061fe]">
                          {testimonial.name}
                        </p>
                        <p className="mt-1 truncate font-inter-tight text-sm font-medium leading-[19.2px] text-[#eaeaea]">
                          {testimonial.role}
                        </p>
                      </div>
                    </div>
                    <p className="mt-5 font-inter-tight text-lg font-medium leading-[21.6px] tracking-[-0.16px] text-[#788aae]">
                      {testimonial.quote}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </Marquee>
          ))}
        </div>
      </div>
    </section>
  );
};
